<script setup>
import { ref, watch } from 'vue';
import { useQuasar } from 'quasar';
import { UserService } from '@/services';
import { useEventStore } from '@/stores/event/event.store';
import { InputSelect } from '@/components/inputs';
import fontList from '@/components/admin/fontList.js';
import { EventVersionList } from 'interface/data/event/versions';

const props = defineProps({
  clientId: {
    type: String,
    required: true,
  },
});

const quasar = useQuasar();
const eventStore = useEventStore();

const additionalSitesNum = ref(eventStore.getAdditionalSites().common.length);

watch(
  () => eventStore.clientData.common,
  async () => {
    additionalSitesNum.value = eventStore.getAdditionalSites().common.length;
    placesNum.value = eventStore.getPlaces().common.length;
  },
);

watch(additionalSitesNum, async (number) => {
  if (
    number === undefined ||
    number === '' ||
    isNaN(number) ||
    number < 0 ||
    number > 10
  ) {
    additionalSitesNum.value = 0;
  }

  if (number > eventStore.getAdditionalSites().common.length) {
    const num = number - eventStore.getAdditionalSites().common.length;
    for (let i = 0; i < num; ++i) {
      eventStore.getAdditionalSites().common.push({
        mainColor: '',
        backgroundColor: '',
      });
      eventStore.getAdditionalSites().lang.push({
        header: '',
        buttonText: '',
        subheading: '',
      });
    }
  } else if (number < eventStore.getAdditionalSites().common.length) {
    const num = eventStore.getAdditionalSites().common.length - number;
    for (let i = 0; i < num; ++i) {
      eventStore.getAdditionalSites().common.pop();
      eventStore.getAdditionalSites().lang.pop();
    }
  }
});

const placesNum = ref(eventStore.getPlaces().common.length);

watch(placesNum, async (number) => {
  if (
    number === undefined ||
    number === '' ||
    isNaN(number) ||
    number < 0 ||
    number > 3
  ) {
    placesNum.value = 0;
  }

  if (number > eventStore.getPlaces().common.length) {
    const num = number - eventStore.getPlaces().common.length;
    for (let i = 0; i < num; ++i) {
      eventStore.getPlaces().common.push({
        link: '',
      });
      eventStore.getPlaces().lang.push({
        name: '',
      });
    }
  } else if (number < eventStore.getPlaces().common.length) {
    const num = eventStore.getPlaces().common.length - number;
    for (let i = 0; i < num; ++i) {
      eventStore.clientData.common.places.pop();
    }
  }
});

async function sendEventData() {
  if (props.clientId) {
    const res = await UserService.sendEventCommonData(
      props.clientId,
      eventStore.clientData.common,
    );
    if (res) {
      quasar.notify({
        type: 'info',
        message: `Wysłano dane klienta`,
      });
    } else {
      quasar.notify({
        type: 'negative',
        message: `Błąd w przesyle danych`,
      });
    }
  } else {
    quasar.notify({
      type: 'negative',
      message: `Nie wybrano wszystkich pól`,
    });
  }
}
</script>

<template>
  <q-form ref="ClientForm" class="form" @submit.prevent="sendEventData">
    <div class="send">
      <button class="core-default-form-button" type="submit">Wyślij</button>
    </div>
    <InputSelect
      v-model="eventStore.getCommonData.version"
      class="input"
      :select-options="Object.values(EventVersionList)"
      main-color="#84491f"
      background-color="#fffbf8"
      text-font="Raleway"
      >Wersja</InputSelect
    >
    <div class="inputs">
      <div class="input">
        <p class="label">Czcionka nagówka</p>
        <q-select
          v-model="eventStore.getCommonData.headerFont"
          borderless
          class="core-default-input-form core-default-input-form"
          :options="fontList"
        />
      </div>
      <div class="input">
        <p class="label">Główny kolor</p>
        <q-input
          v-model="eventStore.getCommonData.mainColor"
          class="core-default-input-form"
          borderless
          autogrow
          lazy-rules
          :rules="[(val) => !!val || 'To pole nie może być puste', 'anyColor']"
        >
          <template #append>
            <q-icon name="colorize" class="cursor-pointer">
              <q-popup-proxy
                cover
                transition-show="scale"
                transition-hide="scale"
              >
                <q-color v-model="eventStore.getCommonData.mainColor" />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
      </div>
      <div class="input">
        <p class="label">Kolor przycisku</p>
        <q-input
          v-model="eventStore.getCommonData.buttonColor"
          class="core-default-input-form"
          borderless
          autogrow
          lazy-rules
          :rules="[(val) => !!val || 'anyColor']"
        >
          <template #append>
            <q-icon name="colorize" class="cursor-pointer">
              <q-popup-proxy
                cover
                transition-show="scale"
                transition-hide="scale"
              >
                <q-color v-model="eventStore.getCommonData.buttonColor" />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
      </div>
      <div class="input">
        <p class="label">Kolor tła</p>
        <q-input
          v-model="eventStore.getCommonData.backgroundColor"
          class="core-default-input-form"
          borderless
          autogrow
          lazy-rules
          :rules="[(val) => !!val || 'To pole nie może być puste', 'anyColor']"
        >
          <template #append>
            <q-icon name="colorize" class="cursor-pointer">
              <q-popup-proxy
                cover
                transition-show="scale"
                transition-hide="scale"
              >
                <q-color v-model="eventStore.getCommonData.backgroundColor" />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
      </div>
      <div class="input">
        <p class="label">Czcionka</p>
        <q-select
          v-model="eventStore.getCommonData.textFont"
          borderless
          class="core-default-input-form core-default-input-form"
          :options="fontList"
        />
      </div>
      <div>
        <div class="input">
          <p class="label">Liczba podstron</p>
          <div class="slider">
            <q-slider
              v-model="additionalSitesNum"
              class="core-default-input-form slider-form"
              :min="0"
              :max="10"
              label
              switch-label-side
              color="primary"
              track-color="accent"
              thumb-color="accent"
              label-color="accent"
            />
            <q-input
              v-model="additionalSitesNum"
              class="core-default-input-form"
              borderless
              autogrow
            />
          </div>
        </div>
        <div v-if="additionalSitesNum !== 0" class="row-area-container"></div>
        <div
          v-for="i in 10"
          v-show="i <= additionalSitesNum"
          :key="i"
          class="row-area-container"
        >
          <div v-if="i <= additionalSitesNum" class="row-area">
            <div class="input">
              <p class="label">Główny kolor</p>
              <q-input
                v-model="
                  eventStore.getAdditionalSitesById(i - 1).common.mainColor
                "
                class="core-default-input-form"
                borderless
                autogrow
              >
                <template #append>
                  <q-icon name="colorize" class="cursor-pointer">
                    <q-popup-proxy
                      cover
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-color
                        v-model="
                          eventStore.getAdditionalSitesById(i - 1).common
                            .mainColor
                        "
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
            <div class="input">
              <p class="label">Kolor tła</p>
              <q-input
                v-model="
                  eventStore.getAdditionalSitesById(i - 1).common
                    .backgroundColor
                "
                class="core-default-input-form"
                borderless
                autogrow
              >
                <template #append>
                  <q-icon name="colorize" class="cursor-pointer">
                    <q-popup-proxy
                      cover
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-color
                        v-model="
                          eventStore.getAdditionalSitesById(i - 1).common
                            .backgroundColor
                        "
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="input">
          <p class="label">Liczba miejsc</p>
          <div class="slider">
            <q-slider
              v-model="placesNum"
              class="core-default-input-form slider-form"
              :min="0"
              :max="3"
              label
              switch-label-side
              color="primary"
              track-color="accent"
              thumb-color="accent"
              label-color="accent"
            />
            <q-input
              v-model="placesNum"
              class="core-default-input-form"
              borderless
              autogrow
            />
          </div>
        </div>
        <div v-if="placesNum !== 0" class="row-area-container"></div>
        <div
          v-for="i in 3"
          v-show="i <= placesNum"
          :key="i"
          class="row-area-container"
        >
          <div v-if="i <= placesNum" class="row-area">
            <div class="input">
              <p class="label">Link do map</p>
              <q-input
                v-model="eventStore.getPlacesById(i - 1).common.link"
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
                :rules="[(val) => !!val || 'To pole nie może być puste']"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </q-form>
</template>

<style lang="scss" scoped>
@import '@/components/admin/form';
</style>
